import React from 'react';
import { graphql } from 'gatsby';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductCategoryListing from '../components/ProductCategoryListing';
import ProductListing from '../components/ProductListing';
import SubBanner from '../components/SubBanner';
import FeaturedProducts from '../components/FeaturedProducts';
import ModularBlocks from '../components/ModularBlocks';
import { getAllProductCategoryItems, orderFeaturedProducts } from '../utils';

const ProductCategoryPageTemplate = ({
  data: {
    datoCmsProductCategory,
    shopifyCollection,
    allShopifyCollection,
    allShopifyProduct: { nodes: featuredProducts },
  },
  pageContext: { featuredProducts: featuredProductsOrder },
}) => {
  const {
    seoMetaTags,
    title,
    treeParent,
    bannerHeading,
    bannerText,
    bannerImage,
    bannerBackgroundColour,
    removeBreadcrumbs,
    treeChildren,
    additionalItems,
    featuredProductsHeading,
    modularBlocks,
  } = datoCmsProductCategory;

  const productCategoryItems = getAllProductCategoryItems(
    treeChildren.filter(({ removeFromListing }) => !removeFromListing),
    additionalItems.map(
      ({ item, largeImage, isFeatured, positionOverride }) => ({
        ...item,
        largeImage: largeImage,
        isFeatured: isFeatured,
        positionOverride: positionOverride,
      }),
    ),
    allShopifyCollection.nodes,
  );

  const hasProductCategoryItems = productCategoryItems.length > 0;
  const hasProductItems =
    shopifyCollection && shopifyCollection.products.length > 0;

  return (
    <Layout seo={seoMetaTags}>
      <main>
        {!removeBreadcrumbs && (
          <Breadcrumbs currentPageTitle={title} items={treeParent} />
        )}
        {hasProductCategoryItems ? (
          <ProductCategoryListing
            heading={bannerHeading || title}
            text={bannerText}
            items={productCategoryItems}
          />
        ) : hasProductItems ? (
          <ProductListing
            heading={bannerHeading || title}
            text={bannerText}
            handle={shopifyCollection.handle}
            items={shopifyCollection.products}
            productBreadcrumbs={datoCmsProductCategory}
          />
        ) : (
          <SubBanner
            heading={bannerHeading || title}
            text={bannerText}
            image={bannerImage}
            backgroundColour={bannerBackgroundColour}
          />
        )}
        <FeaturedProducts
          heading={featuredProductsHeading || 'Featured Products'}
          items={orderFeaturedProducts(featuredProducts, featuredProductsOrder)}
          alt={true}
        />
        <ModularBlocks items={modularBlocks} />
      </main>
    </Layout>
  );
};

export const ProductCategoryPageQuery = graphql`
  query ProductCategoryPageTemplateQuery(
    $id: String!
    $handle: String!
    $subcategories: [String]
    $featuredProducts: [String]
  ) {
    datoCmsProductCategory(id: { eq: $id }) {
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      title
      treeParent {
        title
        ...LinkFragment
        treeParent {
          title
          ...LinkFragment
          treeParent {
            title
            ...LinkFragment
          }
        }
      }
      bannerHeading
      bannerText {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
      bannerImage {
        ...SubBannerImageFragment
      }
      bannerBackgroundColour {
        hex
      }
      removeBreadcrumbs
      treeChildren {
        ...ProductCategoryCardFragment
        largeImage: featuredImageLarge {
          gatsbyImageData(width: 2120, imgixParams: { w: "2120" })
          alt
        }
        isFeatured
        removeFromListing
        position
      }
      additionalItems {
        item {
          ... on DatoCmsPage {
            heading: title
            image: featuredImage {
              gatsbyImageData(width: 300, imgixParams: { w: "300" })
              alt
            }
            ...LinkFragment
          }
          ... on DatoCmsProductCategory {
            ...ProductCategoryCardFragment
          }
          ... on DatoCmsListingCallToAction {
            ...ListingCtaCardFragment
          }
        }
        largeImage: featuredImageLarge {
          gatsbyImageData(width: 2400, imgixParams: { w: "2400" })
          alt
        }
        isFeatured
        positionOverride
      }
      featuredProductsHeading
      modularBlocks {
        ...AccordionModularBlockFragment
        ...CtaCardsModularBlockFragment
        ...CallbackFormModularBlockFragment
        ...ContentBlocksModularBlockFragment
        ...ContentModularBlockFragment
        ...DisplayStandsModularBlockFragment
        ...ExternalVideoModularBlockFragment
        ...FeaturedLogosModularBlockFragment
        ...FeaturedProductCategoriesModularBlockFragment
        ...ImageContentBlocksModularBlockFragment
        ...ImageContentModularBlockV2Fragment
        ...InternalVideoModularBlockFragment
        ...LedupConfiguratorModularBlockFragment
        ...PriceCardsModularBlockFragment
      }
      ...LinkFragment
    }
    shopifyCollection(handle: { eq: $handle }) {
      handle
      products {
        ...ProductCardFragment
      }
    }
    allShopifyCollection(filter: { handle: { in: $subcategories } }) {
      nodes {
        handle
        products {
          priceRangeV2 {
            minVariantPrice {
              amount
            }
          }
          m2Pricing: metafield(key: "m2_pricing", namespace: "custom") {
            value
          }
        }
      }
    }
    allShopifyProduct(filter: { handle: { in: $featuredProducts } }) {
      nodes {
        ...ProductCardFragment
      }
    }
  }
`;

export default ProductCategoryPageTemplate;
